import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useStudentFocus } from '@/admin/studentFocus';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { StudentListHeader } from '@/admin/ui/ContentNav';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type QuizResponseSummary = components['schemas']['QuizResponseSummary'];

type QuizSubmissionsSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/admin/quiz-submissions/')({
  component: QuizSubmissionsList,
  validateSearch: (search: Record<string, unknown>): QuizSubmissionsSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<QuizResponseSummary>();

function statusBadge(row: QuizResponseSummary): {
  label: string;
  variant: BadgeVariant;
} {
  if (!row.complete)
    return {
      label: i18n.t('admin.quizSubmissions.status.inProgress'),
      variant: 'neutral',
    };
  if (row.passed)
    return {
      label: i18n.t('admin.quizSubmissions.status.passed'),
      variant: 'success',
    };
  return {
    label: i18n.t('admin.quizSubmissions.status.failed'),
    variant: 'error',
  };
}

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('user_display_name', {
    header: () => i18n.t('admin.quizSubmissions.col.student'),
    cell: (info) => (
      <div>
        <div>{info.getValue()}</div>
        <small style={{ color: 'var(--grey-600)' }}>
          {info.row.original.user_email}
        </small>
      </div>
    ),
  }),
  columnHelper.accessor('course_name', {
    header: () => i18n.t('admin.quizSubmissions.col.course'),
  }),
  columnHelper.accessor('lesson_name', {
    header: () => i18n.t('admin.quizSubmissions.col.lesson'),
  }),
  columnHelper.accessor('score', {
    header: () => i18n.t('admin.quizSubmissions.col.score'),
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.display({
    id: 'status',
    header: () => i18n.t('admin.common.status'),
    cell: (info) => {
      const { label, variant } = statusBadge(info.row.original);
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.accessor('created_at', {
    header: () => i18n.t('admin.quizSubmissions.col.submitted'),
    cell: (info) => new Date(info.getValue()).toLocaleString(),
  }),
];

function QuizSubmissionsList() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listQuizResponses.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {search.user_id !== undefined ? (
        <StudentListHeader
          studentId={search.user_id}
          title={t('admin.quizSubmissions.studentListTitle')}
        />
      ) : (
        <PageTitle>{t('admin.nav.quizSubmissions')}</PageTitle>
      )}

      {isLoading ? (
        <LoadingMessage>{t('admin.quizSubmissions.loading')}</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{t('admin.quizSubmissions.errorLoading')}</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table
            table={table}
            to="/admin/quiz-submissions/$submissionId/"
            getParams={(row) => ({ submissionId: String(row.id) })}
          />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
